import { useState } from "react";
import { MessagesSquare, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Drop a story into a room — everyone reads it in their own language.
export const DiscussButton = ({ story, className, big }) => {
  const [rooms, setRooms] = useState(null);
  const [sending, setSending] = useState("");
  const [sent, setSent] = useState("");

  const load = async (open) => {
    if (!open || rooms) return;
    try {
      const { data } = await api.get("/rooms");
      setRooms(data.rooms || data || []);
    } catch (e) {
      console.error("could not load rooms", e);
      setRooms([]);
    }
  };

  const share = async (room) => {
    setSending(room.room_id);
    try {
      let link = story.url;
      try {  // Google News hides the publisher behind a redirect — send the real address
        const { data } = await api.post("/news/link",
                                       { story_id: story.story_id, url: story.url });
        link = data.url || story.url;
      } catch {
        link = story.url;
      }
      await api.post(`/rooms/${room.room_id}/messages`, { text: `${story.title}\n${link}` });
      setSent(room.room_id);
      toast.success(`Shared with ${room.name}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not share that");
    } finally {
      setSending("");
    }
  };

  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <button data-testid={`discuss-${story.story_id}`} className={className}>
          <MessagesSquare className={big ? "h-[8.1px] w-[8.1px]" : "h-[6.7px] w-[6.7px]"} /> Discuss
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 border-white/10 bg-panel p-2"
                      data-testid="discuss-rooms">
        <p className="px-2 py-1 text-[11px] uppercase tracking-widest text-ink-soft">
          Send to a conversation
        </p>
        {rooms === null && <p className="px-2 py-2 text-xs text-ink-soft">Loading…</p>}
        {rooms?.length === 0 && (
          <p className="px-2 py-2 text-xs text-ink-soft">
            No conversations yet — start one in iMessages.
          </p>
        )}
        {(rooms || []).map((room) => (
          <button
            key={room.room_id}
            data-testid={`discuss-room-${room.room_id}`}
            onClick={() => share(room)}
            disabled={Boolean(sending)}
            className="flex w-full items-center gap-2 rounded-xl px-2 py-2 text-start text-sm transition-colors hover:bg-white/5 disabled:opacity-50"
          >
            <span className="min-w-0 flex-1 truncate" dir="auto">{room.name}</span>
            {sending === room.room_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />}
            {sent === room.room_id && <Check className="h-3.5 w-3.5 text-brand" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
